/**
 * animations.js — scroll-reveal via IntersectionObserver.
 *
 * Anything tagged `[data-animate-up]` starts hidden + slightly translated,
 * then transitions in when it enters the viewport. One-shot — once
 * revealed, the observer stops watching so back-scroll doesn't re-trigger.
 *
 * `prefers-reduced-motion: reduce` short-circuits via CSS (in theme.css);
 * we still mark elements as visible so layout never gets stuck hidden.
 */

(() => {
  const SELECTOR = '[data-animate-up]';
  const REVEAL_ATTR = 'data-animate-state';
  const REVEAL_VALUE = 'visible';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const reveal = (el) => el.setAttribute(REVEAL_ATTR, REVEAL_VALUE);

  // Add .has-anim only if we can confirm IntersectionObserver will fire reliably.
  // Otherwise leave the page in default-visible state (graceful degradation).
  // Fallback timer reveals everything after 1500ms even if observer never fires —
  // ensures screenshot tools and slow connections don't end up with hidden content.
  if (!reduceMotion && 'IntersectionObserver' in window) {
    document.documentElement.classList.add('has-anim');
    setTimeout(() => {
      document.querySelectorAll(SELECTOR).forEach(reveal);
    }, 1500);
  }

  function init() {
    const targets = document.querySelectorAll(SELECTOR);
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(reveal);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            reveal(entry.target);
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -10% 0px' }
    );

    targets.forEach((el) => io.observe(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-bind newly rendered sections.
  document.addEventListener('shopify:section:load', () => init());

  /* ============================================================
   * Brand-vocab protection — Shopify Translate & Adapt and Google
   * Translate auto-render "cacao" → "cocoa" in EN. CAÚA wants
   * "cacao" preserved in BOTH locales because it's the brand
   * product term. Walk text nodes once after load, find the brand
   * vocabulary, and wrap each match in a <span translate="no">.
   * ============================================================ */
  const BRAND_RE = /\b(CAÚA|caúa|CAUA|caua|Cacao|cacao|Theobroma|theobroma)\b/g;

  function protectBrandVocab(root) {
    if (!root) return;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!node.parentNode) return NodeFilter.FILTER_REJECT;
        const tag = node.parentNode.nodeName;
        if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'NOSCRIPT') return NodeFilter.FILTER_REJECT;
        if (node.parentNode.closest && node.parentNode.closest('[translate="no"]')) return NodeFilter.FILTER_REJECT;
        if (!BRAND_RE.test(node.nodeValue)) return NodeFilter.FILTER_REJECT;
        BRAND_RE.lastIndex = 0;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    const targets = [];
    let n;
    while ((n = walker.nextNode())) targets.push(n);
    for (const text of targets) {
      const parent = text.parentNode;
      if (!parent) continue;
      const frag = document.createDocumentFragment();
      let lastIndex = 0;
      const value = text.nodeValue;
      BRAND_RE.lastIndex = 0;
      let match;
      while ((match = BRAND_RE.exec(value)) !== null) {
        if (match.index > lastIndex) {
          frag.appendChild(document.createTextNode(value.slice(lastIndex, match.index)));
        }
        const span = document.createElement('span');
        span.setAttribute('translate', 'no');
        span.textContent = match[0];
        frag.appendChild(span);
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < value.length) {
        frag.appendChild(document.createTextNode(value.slice(lastIndex)));
      }
      parent.replaceChild(frag, text);
    }
  }

  function runBrandProtect() {
    try { protectBrandVocab(document.body); } catch (_) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runBrandProtect);
  } else {
    runBrandProtect();
  }
  document.addEventListener('shopify:section:load', (e) => {
    try { protectBrandVocab(e.target); } catch (_) {}
  });

  /* ============================================================
   * Nutrition-label dialog — Gruns-style "View Nutrition Label"
   * pattern, native HTML5 <dialog>. Trigger button has
   * data-nutrition-trigger="<dialog id>"; close buttons have
   * data-nutrition-close. Backdrop click + ESC also close.
   * ============================================================ */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-nutrition-trigger]');
    if (trigger) {
      const id = trigger.getAttribute('data-nutrition-trigger');
      const dlg = id ? document.getElementById(id) : null;
      if (dlg && typeof dlg.showModal === 'function') {
        event.preventDefault();
        dlg.showModal();
      }
      return;
    }
    const closer = event.target.closest('[data-nutrition-close]');
    if (closer) {
      const dlg = closer.closest('dialog');
      if (dlg) dlg.close();
      return;
    }
    // Backdrop click — close when clicking the dialog element itself
    // (not the inner panel). Native <dialog> ::backdrop is the gap.
    if (event.target.tagName === 'DIALOG' && event.target.classList.contains('caua-nutrition-dialog')) {
      event.target.close();
    }
  });
})();
